import styled from 'styled-components';

import { useDispatch, useSelector } from 'react-redux';
import { NodeTrace, State, tabActions } from '@store/index';
import { Alert, Stack, Tab } from '@mui/material';
import { WorkflowTrace } from '@common/Node/CompletionChatbot/WorkflowTrace';
import { useParams } from 'react-router-dom';
import { MutableRefObject, useEffect, useState } from 'react';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { TabContainer } from '@common/Node/CompletionChatbot';
import { useTheme } from '../../../../../../styles';
import { MessageChats } from './MessageChats';

const Container = styled.div`
  display: flex;
  flex-direction: column;

  & .MuiTabPanel-root {
    padding: 0;
  }
`;

interface Props {
  abortControllerRef: MutableRefObject<AbortController | null>;
  start: () => void;
  pause: () => void;
  reset: () => void;
}

export const MessageClient: React.FC<Props> = ({ abortControllerRef, start, pause, reset }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { workflow_id } = useParams();
  const [tab, setTab] = useState('1');

  const trace = useSelector((state: State) => state.tabs.active.run);
  const commands = useSelector((state: State) => state.tabs.active.commands);
  const tracesWithoutModules = trace.traces.filter(
    (trace) => trace?.metadata?.workflow_id === workflow_id || !trace?.metadata?.workflow_id
  );

  useEffect(() => {
    // handles opening the messaging tab when a response is sent
    if (trace.events.openMessaging.caught && !trace.events.openMessaging.triggered) {
      dispatch(tabActions.updateTraceEvent({ event: 'openMessaging' as const, field: 'triggered', value: true }));
      setTab('2');
    }
  }, [trace.events]);

  return (
    <Container>
      <TabContext value={tab}>
        <TabContainer color={theme.primary}>
          <TabList onChange={(_, newTab) => setTab(newTab)}>
            <Tab disableRipple label='Events' value='1' />
            <Tab disableRipple label='Messages' value='2' />
          </TabList>
        </TabContainer>
        <TabPanel value='1'>
          {(trace?.running || trace?.id) && trace.traces.length === 0 ? (
            <Stack padding='16px 20px'>
              <Alert severity='info'>The workflow is starting. You will see events show here shortly.</Alert>
            </Stack>
          ) : (
            <Stack padding='16px 20px' spacing={1.5}>
              {tracesWithoutModules.map((item: NodeTrace, index) => {
                const command = commands.find((command) => command.node.id === item?.metadata?.node_id);
                const isModNode = command?.config?.command_id === 'node.module';

                const moduleTraces = isModNode
                  ? trace.traces.filter((trace) => trace?.metadata?.workflow_id === command?.state?.moduleId)
                  : [];

                return <WorkflowTrace key={index} trace={item} moduleTraces={moduleTraces} />;
              })}
            </Stack>
          )}
        </TabPanel>
        <TabPanel value='2'>
          <MessageChats abortControllerRef={abortControllerRef} start={start} pause={pause} reset={reset} />
        </TabPanel>
      </TabContext>
    </Container>
  );
};
